import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { toast } from 'react-toastify';
import Button from '../../BaseComponents/Button';
import Loader from '../../BaseComponents/Loader';
import { ReactComponent as ClockIcon } from '../../../assets/icons/ClockCircle.svg';
import { ReactComponent as PenIcon } from '../../../assets/icons/Pen.svg';
import {
  generatePersonalisedMonitoringPlan,
  PersonalisedMonitoringPlanResponse,
} from '../../../api/chat';
import {
  getMemberPersonalisedMonitoringPlan,
  getMemberPersonalisedMonitoringPlanHistory,
  MemberPersonalisedMonitoringPlanHistoryItem,
  MemberPersonalisedMonitoringPlanResponse,
  saveMemberPersonalisedMonitoringPlan,
  updateMemberPersonalisedMonitoringPlanName,
} from '../../../api/members';
import AiMarkdownContent from '../MemberInfoCard/AiMarkdownContent';
import './PersonalisedMonitoringPlanTab.scss';

const hasCachedPlan = (
  data: MemberPersonalisedMonitoringPlanResponse | null
): data is MemberPersonalisedMonitoringPlanResponse & { reply: string } => {
  if (!data || data.success === false) return false;
  return typeof data.reply === 'string' && data.reply.trim().length > 0;
};

const applyPlanResponse = (
  data: PersonalisedMonitoringPlanResponse,
  setReportText: (text: string) => void,
  setErrorText: (text: string) => void
): boolean => {
  if (data?.success === false) {
    setErrorText(
      data.reply || 'Personalised monitoring plan cannot be generated with the available data.'
    );
    return false;
  }

  if (!data?.reply || typeof data.reply !== 'string' || data.reply.trim().length === 0) {
    setErrorText('Plan was generated, but no report text was returned.');
    return false;
  }

  setReportText(data.reply.trim());
  return true;
};

const formatPerformedAt = (iso: string): string => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
};

const getPlanName = (
  item: Pick<MemberPersonalisedMonitoringPlanHistoryItem, 'name'> | null | undefined
): string => (typeof item?.name === 'string' ? item.name.trim() : '');

const getReferenceNumber = (value?: string | null): string => value?.trim() || '';

const PersonalisedMonitoringPlanTab = () => {
  const location = useLocation();
  const memberId = location.pathname.split('/')[2];

  const [isLoading, setIsLoading] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [reportText, setReportText] = useState('');
  const [errorText, setErrorText] = useState('');
  const [performedAt, setPerformedAt] = useState('');
  const [selectedPlanName, setSelectedPlanName] = useState('');
  const [selectedReferenceNumber, setSelectedReferenceNumber] = useState('');
  const [selectedPlanId, setSelectedPlanId] = useState<number | null>(null);
  const [historyItems, setHistoryItems] = useState<MemberPersonalisedMonitoringPlanHistoryItem[]>(
    []
  );

  const [isEditingPlan, setIsEditingPlan] = useState(false);
  const [editDraft, setEditDraft] = useState('');
  const [editName, setEditName] = useState('');

  const [renamingPlanId, setRenamingPlanId] = useState<number | null>(null);
  const [renameDraft, setRenameDraft] = useState('');
  const [isRenaming, setIsRenaming] = useState(false);

  const exitEditMode = () => {
    setIsEditingPlan(false);
    setEditDraft('');
    setEditName('');
  };

  const cancelRename = () => {
    setRenamingPlanId(null);
    setRenameDraft('');
  };

  const loadHistory = async (): Promise<MemberPersonalisedMonitoringPlanHistoryItem[]> => {
    if (!memberId) return [];
    const items = await getMemberPersonalisedMonitoringPlanHistory(memberId);
    setHistoryItems(items);
    return items;
  };

  const selectHistoryItem = (item: MemberPersonalisedMonitoringPlanHistoryItem) => {
    if (renamingPlanId === item.id) return;
    exitEditMode();
    cancelRename();
    setErrorText('');
    setReportText(item.reply.trim());
    setPerformedAt(item.performed_at);
    setSelectedPlanName(getPlanName(item));
    setSelectedReferenceNumber(getReferenceNumber(item.reference_number));
    setSelectedPlanId(item.id);
  };

  const generateReport = async () => {
    if (!memberId) return false;

    const data = await generatePersonalisedMonitoringPlan(memberId);
    const ok = applyPlanResponse(data, setReportText, setErrorText);
    if (!ok) return false;

    const items = await loadHistory();
    if (items.length > 0) {
      setSelectedPlanId(items[0].id);
      setPerformedAt(items[0].performed_at);
      setSelectedPlanName(getPlanName(items[0]));
      setSelectedReferenceNumber(getReferenceNumber(items[0].reference_number));
    } else {
      setSelectedPlanId(null);
      setPerformedAt('');
      setSelectedPlanName('');
      setSelectedReferenceNumber('');
    }
    return true;
  };

  const loadReport = async () => {
    if (!memberId) return;
    setIsLoading(true);
    setErrorText('');
    setReportText('');
    setPerformedAt('');
    setSelectedPlanName('');
    setSelectedReferenceNumber('');
    setSelectedPlanId(null);
    setHistoryItems([]);
    exitEditMode();
    cancelRename();

    try {
      const [cached, items] = await Promise.all([
        getMemberPersonalisedMonitoringPlan(memberId),
        getMemberPersonalisedMonitoringPlanHistory(memberId),
      ]);
      setHistoryItems(items);

      if (hasCachedPlan(cached)) {
        setReportText(cached.reply.trim());
        if (cached.performed_at) {
          setPerformedAt(cached.performed_at);
        }
        if (typeof cached.id === 'number') {
          setSelectedPlanId(cached.id);
          const matched = items.find((item) => item.id === cached.id);
          setSelectedPlanName(getPlanName(matched) || getPlanName(cached));
          setSelectedReferenceNumber(
            getReferenceNumber(matched?.reference_number) ||
              getReferenceNumber(cached.reference_number)
          );
        } else if (items.length > 0) {
          setSelectedPlanId(items[0].id);
          setPerformedAt(items[0].performed_at);
          setSelectedPlanName(getPlanName(items[0]));
          setSelectedReferenceNumber(getReferenceNumber(items[0].reference_number));
        } else {
          setSelectedPlanName(getPlanName(cached));
          setSelectedReferenceNumber(getReferenceNumber(cached.reference_number));
        }
        return;
      }

      await generateReport();
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : 'Something went wrong while loading the personalised monitoring plan.';
      toast.error(errorMessage);
      setErrorText(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegenerate = async () => {
    if (!memberId || isRegenerating || isEditingPlan) return;
    setIsRegenerating(true);
    setErrorText('');
    cancelRename();

    try {
      await generateReport();
      toast.success('Monitoring plan regenerated');
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : 'Something went wrong while generating the personalised monitoring plan.';
      toast.error(errorMessage);
      setErrorText(errorMessage);
    } finally {
      setIsRegenerating(false);
    }
  };

  const isBusy = isLoading || isRegenerating || isSaving;

  const handleCopy = async () => {
    if (!reportText || isEditingPlan) return;
    try {
      await navigator.clipboard.writeText(reportText);
      toast.success('Copied to clipboard');
    } catch {
      toast.error('Failed to copy to clipboard');
    }
  };

  const handleStartEdit = () => {
    if (!reportText || isBusy) return;
    cancelRename();
    setEditDraft(reportText);
    setEditName(selectedPlanName || 'Untitled Plan');
    setIsEditingPlan(true);
  };

  const handleCancelEdit = () => {
    exitEditMode();
  };

  const handleSaveEdit = async () => {
    if (!memberId || isSaving) return;
    const trimmedReply = editDraft.trim();
    if (!trimmedReply) {
      toast.error('Plan content cannot be empty');
      return;
    }

    setIsSaving(true);
    setErrorText('');

    try {
      const trimmedName = editName.trim();
      const data = await saveMemberPersonalisedMonitoringPlan(
        memberId,
        trimmedReply,
        trimmedName || undefined
      );

      if (data?.success === false) {
        const message = data.reply || 'Failed to save the monitoring plan.';
        toast.error(message);
        setErrorText(message);
        return;
      }

      const items = await loadHistory();
      exitEditMode();
      if (items.length > 0) {
        setErrorText('');
        setReportText(items[0].reply.trim());
        setPerformedAt(items[0].performed_at);
        setSelectedPlanName(getPlanName(items[0]));
        setSelectedReferenceNumber(getReferenceNumber(items[0].reference_number));
        setSelectedPlanId(items[0].id);
      } else {
        setReportText(trimmedReply);
        setSelectedPlanName(trimmedName);
        setSelectedReferenceNumber('');
        setPerformedAt(new Date().toISOString());
        setSelectedPlanId(null);
      }
      toast.success('Monitoring plan saved as a new current entry');
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : 'Something went wrong while saving the personalised monitoring plan.';
      toast.error(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const handleStartRename = (
    event: React.MouseEvent,
    item: MemberPersonalisedMonitoringPlanHistoryItem
  ) => {
    event.stopPropagation();
    if (isEditingPlan || isBusy) return;
    setRenamingPlanId(item.id);
    setRenameDraft(getPlanName(item));
  };

  const handleSaveRename = async (planId: number) => {
    if (!memberId || isRenaming) return;
    setIsRenaming(true);

    try {
      const updated = await updateMemberPersonalisedMonitoringPlanName(
        memberId,
        planId,
        renameDraft
      );
      const nextName = getPlanName(updated) || renameDraft.trim();

      setHistoryItems((prev) =>
        prev.map((item) => (item.id === planId ? { ...item, name: nextName || null } : item))
      );
      if (selectedPlanId === planId) {
        setSelectedPlanName(nextName);
      }
      cancelRename();
      toast.success(nextName ? 'Plan name updated' : 'Plan name cleared');
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error ? err.message : 'Something went wrong while updating the plan name.';
      toast.error(errorMessage);
    } finally {
      setIsRenaming(false);
    }
  };

  useEffect(() => {
    void loadReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberId]);

  const showActions = !isLoading && !isRegenerating && !errorText && reportText;
  const latestPlanId = historyItems[0]?.id ?? null;
  const isViewingHistorical =
    selectedPlanId !== null && latestPlanId !== null && selectedPlanId !== latestPlanId;

  return (
    <div className="personalised-monitoring-plan-tab">
      <div className="personalised-monitoring-plan-tab__header sideBarPad2">
        <div className="personalised-monitoring-plan-tab__header-text">
          <h2>Personalised monitoring plan</h2>
          <p>
            AI-generated follow-up plan based on this member&apos;s biomarkers, scans, notes, and
            clinical history.
          </p>
        </div>
        {showActions && !isEditingPlan && (
          <div className="personalised-monitoring-plan-tab__actions">
            <Button
              text="Copy"
              onClick={() => void handleCopy()}
              isSecondary={true}
              height="36px"
              paddingTop="8px"
              paddingBottom="8px"
              paddingLeft="16px"
              paddingRight="16px"
              borderRadius="10px"
            />
            <Button
              text="Edit"
              onClick={handleStartEdit}
              isSecondary={true}
              height="36px"
              paddingTop="8px"
              paddingBottom="8px"
              paddingLeft="16px"
              paddingRight="16px"
              borderRadius="10px"
            />
            <Button
              text="Regenerate"
              onClick={() => void handleRegenerate()}
              isSecondary={true}
              variant="refresh"
              isLoading={isRegenerating}
              isDisabled={isRegenerating}
              height="36px"
              paddingTop="8px"
              paddingBottom="8px"
              paddingLeft="16px"
              paddingRight="16px"
              borderRadius="10px"
            />
          </div>
        )}
      </div>

      <div className="personalised-monitoring-plan-tab__layout">
        <div className="personalised-monitoring-plan-tab__panel">
          {isBusy && !isEditingPlan && (
            <div className="personalised-monitoring-plan-tab__loading">
              <Loader />
              <span>
                {isRegenerating
                  ? 'Regenerating personalised monitoring plan...'
                  : isSaving
                    ? 'Saving personalised monitoring plan...'
                    : 'Loading personalised monitoring plan...'}
              </span>
            </div>
          )}

          {!isBusy && errorText && !isEditingPlan && (
            <div className="personalised-monitoring-plan-tab__error">{errorText}</div>
          )}

          {!isBusy && !errorText && reportText && !isEditingPlan && (
            <>
              {(performedAt || selectedPlanName || selectedReferenceNumber) && (
                <div className="personalised-monitoring-plan-tab__meta">
                  <ClockIcon className="personalised-monitoring-plan-tab__meta-icon" aria-hidden />
                  <div className="personalised-monitoring-plan-tab__meta-text">
                    <div className="personalised-monitoring-plan-tab__meta-heading">
                      <span className="personalised-monitoring-plan-tab__meta-label">
                        {isViewingHistorical ? 'Historical plan' : 'Current plan'}
                      </span>
                      {selectedReferenceNumber && (
                        <span className="personalised-monitoring-plan-tab__meta-reference">
                          {selectedReferenceNumber}
                        </span>
                      )}
                    </div>
                    {selectedPlanName && (
                      <span className="personalised-monitoring-plan-tab__meta-name">
                        {selectedPlanName}
                      </span>
                    )}
                    {performedAt && (
                      <span className="personalised-monitoring-plan-tab__meta-value">
                        {formatPerformedAt(performedAt)}
                      </span>
                    )}
                    <span className="personalised-monitoring-plan-tab__meta-hint">
                      {isViewingHistorical
                        ? 'Select Current in History to return to the latest plan, or Edit to create a new version from this plan.'
                        : 'Use Edit to revise this plan, or Regenerate to refresh with the latest member data. Previous plans stay in History.'}
                    </span>
                  </div>
                </div>
              )}
              <AiMarkdownContent
                content={reportText}
                className="personalised-monitoring-plan-tab__report"
                variant="monitoring-plan"
              />
            </>
          )}

          {isEditingPlan && (
            <div className="personalised-monitoring-plan-tab__editor">
              <div className="personalised-monitoring-plan-tab__editor-banner">
                Editing plan — changes will be saved as a new Current history entry.
              </div>
              <textarea
                className="personalised-monitoring-plan-tab__editor-textarea"
                value={editDraft}
                onChange={(e) => setEditDraft(e.target.value)}
                aria-label="Edit monitoring plan content"
                disabled={isSaving}
              />
              <div className="personalised-monitoring-plan-tab__editor-footer">
                <label className="personalised-monitoring-plan-tab__editor-name">
                  <span>Plan name</span>
                  <input
                    type="text"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    placeholder="Untitled Plan"
                    maxLength={120}
                    disabled={isSaving}
                  />
                </label>
                <div className="personalised-monitoring-plan-tab__editor-footer-actions">
                  <Button
                    text="Cancel"
                    onClick={handleCancelEdit}
                    backgroundColor="#ffffff"
                    color="#2D334E"
                    width="120px"
                    isDisabled={isSaving}
                  />
                  <Button
                    text="Save"
                    onClick={() => void handleSaveEdit()}
                    isLoading={isSaving}
                    isDisabled={isSaving || !editDraft.trim()}
                    width="120px"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {!isLoading && !isRegenerating && historyItems.length > 0 && (
          <aside className="personalised-monitoring-plan-tab__history" aria-label="Plan history">
            <div className="personalised-monitoring-plan-tab__history-header">
              <h3>History</h3>
              <p>Every generated or edited plan is saved. Click an entry to view it.</p>
            </div>
            <ul className="personalised-monitoring-plan-tab__history-list">
              {historyItems.map((item, index) => {
                const isSelected = item.id === selectedPlanId;
                const isLatest = index === 0;
                const isRenamingThis = renamingPlanId === item.id;
                const planName = getPlanName(item);
                const referenceNumber = getReferenceNumber(item.reference_number);

                return (
                  <li key={item.id}>
                    {isRenamingThis ? (
                      <div
                        className={`personalised-monitoring-plan-tab__history-item is-renaming${
                          isSelected ? ' is-selected' : ''
                        }`}
                      >
                        <input
                          type="text"
                          className="personalised-monitoring-plan-tab__history-rename-input"
                          value={renameDraft}
                          onChange={(e) => setRenameDraft(e.target.value)}
                          placeholder="Enter a name"
                          maxLength={120}
                          autoFocus
                          disabled={isRenaming}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              void handleSaveRename(item.id);
                            }
                            if (e.key === 'Escape') {
                              e.preventDefault();
                              cancelRename();
                            }
                          }}
                        />
                        <span className="personalised-monitoring-plan-tab__history-item-date">
                          {formatPerformedAt(item.performed_at)}
                        </span>
                        {referenceNumber && (
                          <span className="personalised-monitoring-plan-tab__history-item-reference">
                            {referenceNumber}
                          </span>
                        )}
                        {isLatest && (
                          <span className="personalised-monitoring-plan-tab__history-item-badge">
                            Current
                          </span>
                        )}
                        <div className="personalised-monitoring-plan-tab__history-rename-actions">
                          <button
                            type="button"
                            className="personalised-monitoring-plan-tab__history-rename-btn"
                            onClick={() => void handleSaveRename(item.id)}
                            disabled={isRenaming}
                          >
                            {isRenaming ? 'Saving…' : 'Save'}
                          </button>
                          <button
                            type="button"
                            className="personalised-monitoring-plan-tab__history-rename-btn is-muted"
                            onClick={cancelRename}
                            disabled={isRenaming}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div
                        className={`personalised-monitoring-plan-tab__history-item${
                          isSelected ? ' is-selected' : ''
                        }${isEditingPlan ? ' is-disabled' : ''}`}
                        aria-current={isSelected ? 'true' : undefined}
                      >
                        <button
                          type="button"
                          className="personalised-monitoring-plan-tab__history-item-select"
                          onClick={() => selectHistoryItem(item)}
                          disabled={isEditingPlan}
                        >
                          <span className="personalised-monitoring-plan-tab__history-item-title">
                            {planName || 'Untitled Plan'}
                          </span>
                          <span className="personalised-monitoring-plan-tab__history-item-date">
                            {formatPerformedAt(item.performed_at)}
                          </span>
                          {referenceNumber && (
                            <span className="personalised-monitoring-plan-tab__history-item-reference">
                              {referenceNumber}
                            </span>
                          )}
                          {isLatest && (
                            <span className="personalised-monitoring-plan-tab__history-item-badge">
                              Current
                            </span>
                          )}
                        </button>
                        <button
                          type="button"
                          className="personalised-monitoring-plan-tab__history-rename-trigger"
                          aria-label={planName ? 'Edit plan name' : 'Add plan name'}
                          onClick={(e) => handleStartRename(e, item)}
                          disabled={isEditingPlan || isBusy}
                        >
                          <PenIcon aria-hidden />
                        </button>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </aside>
        )}
      </div>
    </div>
  );
};

export default PersonalisedMonitoringPlanTab;
